import { MouseEvent, ReactNode } from 'react';

import { Box, Typography } from '@mui/material';
import clsx from 'clsx';

import { tokens } from '../../../theme/generated/tokens';
import { SxProps } from '../../../types';
import { mergeSx } from '../../../utils';

import { choiceChipClassNames } from './classNames';
import { ChoiceChipSize } from './types';

type SizeConfig = {
  height: number;
  px: number;
  typographyVariant: 'P10M' | 'P12M';
};

const sizeConfigMap: Record<ChoiceChipSize, SizeConfig> = {
  s: {
    height: 20,
    px: 8,
    typographyVariant: 'P10M',
  },
  m: {
    height: 24,
    px: 8,
    typographyVariant: 'P12M',
  },
};

export type ChoiceChipProps = {
  /** Chip label content */
  label: ReactNode;
  /** Selected state */
  selected?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Size variant */
  size?: ChoiceChipSize;
  /** Click handler — not called when disabled */
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  /** Additional CSS class */
  className?: string;
  /** `data-testid` attribute value */
  testId?: string;
  /** MUI sx prop */
  sx?: SxProps;
};

export const ChoiceChip = ({
  label,
  selected = false,
  disabled = false,
  size = 'm',
  onClick,
  className,
  testId,
  sx,
}: ChoiceChipProps) => {
  const sizeConfig = sizeConfigMap[size];

  return (
    <Box
      className={clsx(
        choiceChipClassNames.root,
        choiceChipClassNames[size],
        selected && choiceChipClassNames.selected,
        disabled && choiceChipClassNames.disabled,
        className
      )}
      data-testid={testId}
      onClick={disabled ? undefined : onClick}
      sx={mergeSx(
        {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: sizeConfig.height,
          px: sizeConfig.px,
          gap: '4px',
          borderRadius: tokens.theme.radius.sm,
          flexShrink: 0,
          width: 'fit-content',
          userSelect: 'none',
          cursor: disabled ? 'default' : 'pointer',
          backgroundColor: selected
            ? tokens.theme.bg.selection.neutral
            : tokens.theme.bg.structural.surfaceRaised,
          color: selected
            ? tokens.theme.fg.selection.onNeutral
            : tokens.theme.fg.structural.primary,
          ...(disabled && {
            backgroundColor: tokens.theme.disabled.surface,
            color: tokens.theme.disabled.fg,
          }),
          ...(!disabled && {
            '&:hover': {
              backgroundColor: selected
                ? tokens.theme.bg.selection.neutralHover
                : tokens.theme.bg.action.surfaceSubtleHover,
            },
          }),
        },
        sx
      )}
    >
      <Typography
        component="span"
        className={choiceChipClassNames.label}
        variant={sizeConfig.typographyVariant}
        sx={{
          color: 'inherit',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

ChoiceChip.displayName = 'ChoiceChip';
